import { BLOCKS, type ToolKind } from "./blocks";
import {
  ARROW,
  BONE,
  BOW,
  CHARCOAL,
  COAL,
  COOKED_BEEF,
  COOKED_MUTTON,
  COOKED_PORKCHOP,
  DIAMOND,
  DIAMOND_AXE,
  DIAMOND_PICKAXE,
  DIAMOND_SHOVEL,
  DIAMOND_SWORD,
  DIAMOND_HELMET,
  DIAMOND_CHESTPLATE,
  DIAMOND_LEGGINGS,
  DIAMOND_BOOTS,
  FEATHER,
  FLINT,
  GOLD_INGOT,
  GUNPOWDER,
  SPIDER_EYE,
  IRON_AXE,
  IRON_INGOT,
  IRON_PICKAXE,
  IRON_SHOVEL,
  IRON_SWORD,
  IRON_HELMET,
  IRON_CHESTPLATE,
  IRON_LEGGINGS,
  IRON_BOOTS,
  ITEM_ID_BASE,
  LEATHER,
  LEATHER_HELMET,
  LEATHER_CHESTPLATE,
  LEATHER_LEGGINGS,
  LEATHER_BOOTS,
  RAW_BEEF,
  RAW_MUTTON,
  RAW_PORKCHOP,
  STICK,
  STONE_AXE,
  STONE_PICKAXE,
  STONE_SHOVEL,
  STONE_SWORD,
  STRING,
  WOODEN_AXE,
  WOODEN_PICKAXE,
  WOODEN_SHOVEL,
  WOODEN_SWORD,
} from "./item-ids";

/**
 * Item registry.
 *
 * Placeable blocks double as items today: block id == item id. Non-block items
 * claim ids from ITEM_ID_BASE upward (see `item-ids.ts`). Tools carry
 * per-instance durability in `ItemStack.durability`; every other item ignores
 * that field.
 */

export {
  ARROW,
  BONE,
  BOW,
  CHARCOAL,
  COAL,
  COOKED_BEEF,
  COOKED_MUTTON,
  COOKED_PORKCHOP,
  DIAMOND,
  DIAMOND_AXE,
  DIAMOND_PICKAXE,
  DIAMOND_SHOVEL,
  DIAMOND_SWORD,
  DIAMOND_HELMET,
  DIAMOND_CHESTPLATE,
  DIAMOND_LEGGINGS,
  DIAMOND_BOOTS,
  FEATHER,
  FLINT,
  GOLD_INGOT,
  GUNPOWDER,
  SPIDER_EYE,
  IRON_AXE,
  IRON_INGOT,
  IRON_PICKAXE,
  IRON_SHOVEL,
  IRON_SWORD,
  IRON_HELMET,
  IRON_CHESTPLATE,
  IRON_LEGGINGS,
  IRON_BOOTS,
  ITEM_ID_BASE,
  LEATHER,
  LEATHER_HELMET,
  LEATHER_CHESTPLATE,
  LEATHER_LEGGINGS,
  LEATHER_BOOTS,
  RAW_BEEF,
  RAW_MUTTON,
  RAW_PORKCHOP,
  STICK,
  STONE_AXE,
  STONE_PICKAXE,
  STONE_SHOVEL,
  STONE_SWORD,
  STRING,
  WOODEN_AXE,
  WOODEN_PICKAXE,
  WOODEN_SHOVEL,
  WOODEN_SWORD,
};

export interface ItemStack {
  readonly id: number;
  readonly count: number;
  /** Remaining uses for a tool. Omitted for stackable items. */
  readonly durability?: number;
}

/** Every stackable item stacks to 64 until per-item limits exist. */
export const DEFAULT_STACK_LIMIT = 64;

export interface ToolInfo {
  readonly kind: ToolKind;
  /** 1 = wood, 2 = stone, 3 = iron, 4 = diamond. Harvest rules compare against this. */
  readonly tier: number;
  /** Mining-speed multiplier on the tool's preferred blocks. */
  readonly speed: number;
  readonly maxDurability: number;
}

export type ArmorSlot = "helmet" | "chestplate" | "leggings" | "boots";
export type ArmorTier = "leather" | "iron" | "diamond";

export interface ArmorInfo {
  readonly slot: ArmorSlot;
  readonly tier: ArmorTier;
  /** Defense points (each point is 4% damage reduction, 1 point = half chestplate icon). */
  readonly defense: number;
  readonly maxDurability: number;
}

/** Edible items restore hunger and saturation according to Minecraft Java Edition 1.8.8. */
export interface FoodInfo {
  /** Food points restored (1 to 20, 1 point = half a drumstick, 2 = full drumstick). */
  readonly foodLevel: number;
  /** Saturation modifier (Minecraft formula: foodLevel * saturationModifier * 2.0). */
  readonly saturationModifier: number;
  /** Whether the food can be eaten even if food is full (e.g. golden apples). */
  readonly alwaysEdible?: boolean;
}

/** Non-block item definition. Block items read their data from `BLOCKS`. */
export interface ItemDef {
  readonly id: number;
  readonly name: string;
  readonly maxStack: number;
  readonly tool?: ToolInfo;
  readonly armor?: ArmorInfo;
  readonly food?: FoodInfo;
}

function toolDef(
  id: number,
  name: string,
  kind: ToolKind,
  tier: number,
  speed: number,
  maxDurability: number,
): ItemDef {
  return { id, name, maxStack: 1, tool: { kind, tier, speed, maxDurability } };
}

function armorDef(
  id: number,
  name: string,
  slot: ArmorSlot,
  tier: ArmorTier,
  defense: number,
  maxDurability: number,
): ItemDef {
  return { id, name, maxStack: 1, armor: { slot, tier, defense, maxDurability } };
}

function foodDef(
  id: number,
  name: string,
  foodLevel: number,
  saturationModifier: number,
  alwaysEdible = false,
): ItemDef {
  return {
    id,
    name,
    maxStack: DEFAULT_STACK_LIMIT,
    food: { foodLevel, saturationModifier, alwaysEdible },
  };
}

const ITEM_DEFS: Readonly<Partial<Record<number, ItemDef>>> = {
  [STICK]: { id: STICK, name: "Stick", maxStack: DEFAULT_STACK_LIMIT },
  [WOODEN_SWORD]: toolDef(WOODEN_SWORD, "Wooden Sword", "sword", 1, 1.5, 59),
  [WOODEN_PICKAXE]: toolDef(WOODEN_PICKAXE, "Wooden Pickaxe", "pickaxe", 1, 2, 59),
  [WOODEN_AXE]: toolDef(WOODEN_AXE, "Wooden Axe", "axe", 1, 2, 59),
  [WOODEN_SHOVEL]: toolDef(WOODEN_SHOVEL, "Wooden Shovel", "shovel", 1, 2, 59),
  [STONE_SWORD]: toolDef(STONE_SWORD, "Stone Sword", "sword", 2, 1.5, 131),
  [STONE_PICKAXE]: toolDef(STONE_PICKAXE, "Stone Pickaxe", "pickaxe", 2, 4, 131),
  [STONE_AXE]: toolDef(STONE_AXE, "Stone Axe", "axe", 2, 4, 131),
  [STONE_SHOVEL]: toolDef(STONE_SHOVEL, "Stone Shovel", "shovel", 2, 4, 131),
  [IRON_SWORD]: toolDef(IRON_SWORD, "Iron Sword", "sword", 3, 1.5, 250),
  [IRON_PICKAXE]: toolDef(IRON_PICKAXE, "Iron Pickaxe", "pickaxe", 3, 6, 250),
  [IRON_AXE]: toolDef(IRON_AXE, "Iron Axe", "axe", 3, 6, 250),
  [IRON_SHOVEL]: toolDef(IRON_SHOVEL, "Iron Shovel", "shovel", 3, 6, 250),
  [DIAMOND_SWORD]: toolDef(DIAMOND_SWORD, "Diamond Sword", "sword", 4, 1.5, 1561),
  [DIAMOND_PICKAXE]: toolDef(DIAMOND_PICKAXE, "Diamond Pickaxe", "pickaxe", 4, 8, 1561),
  [DIAMOND_AXE]: toolDef(DIAMOND_AXE, "Diamond Axe", "axe", 4, 8, 1561),
  [DIAMOND_SHOVEL]: toolDef(DIAMOND_SHOVEL, "Diamond Shovel", "shovel", 4, 8, 1561),
  [COAL]: { id: COAL, name: "Coal", maxStack: DEFAULT_STACK_LIMIT },
  [CHARCOAL]: { id: CHARCOAL, name: "Charcoal", maxStack: DEFAULT_STACK_LIMIT },
  [IRON_INGOT]: { id: IRON_INGOT, name: "Iron Ingot", maxStack: DEFAULT_STACK_LIMIT },
  [GOLD_INGOT]: { id: GOLD_INGOT, name: "Gold Ingot", maxStack: DEFAULT_STACK_LIMIT },
  [DIAMOND]: { id: DIAMOND, name: "Diamond", maxStack: DEFAULT_STACK_LIMIT },
  [RAW_BEEF]: foodDef(RAW_BEEF, "Raw Beef", 3, 0.3),
  [COOKED_BEEF]: foodDef(COOKED_BEEF, "Steak", 8, 0.8),
  [RAW_PORKCHOP]: foodDef(RAW_PORKCHOP, "Raw Porkchop", 3, 0.3),
  [COOKED_PORKCHOP]: foodDef(COOKED_PORKCHOP, "Cooked Porkchop", 8, 0.8),
  [RAW_MUTTON]: foodDef(RAW_MUTTON, "Raw Mutton", 2, 0.3),
  [COOKED_MUTTON]: foodDef(COOKED_MUTTON, "Cooked Mutton", 6, 0.8),
  [BOW]: toolDef(BOW, "Bow", "bow", 1, 1, 384),
  [ARROW]: { id: ARROW, name: "Arrow", maxStack: DEFAULT_STACK_LIMIT },
  [STRING]: { id: STRING, name: "String", maxStack: DEFAULT_STACK_LIMIT },
  [FEATHER]: { id: FEATHER, name: "Feather", maxStack: DEFAULT_STACK_LIMIT },
  [FLINT]: { id: FLINT, name: "Flint", maxStack: DEFAULT_STACK_LIMIT },
  [BONE]: { id: BONE, name: "Bone", maxStack: DEFAULT_STACK_LIMIT },
  [LEATHER]: { id: LEATHER, name: "Leather", maxStack: DEFAULT_STACK_LIMIT },
  [GUNPOWDER]: { id: GUNPOWDER, name: "Gunpowder", maxStack: DEFAULT_STACK_LIMIT },
  [SPIDER_EYE]: foodDef(SPIDER_EYE, "Spider Eye", 2, 0.8),
  // Leather Armor
  [LEATHER_HELMET]: armorDef(LEATHER_HELMET, "Leather Cap", "helmet", "leather", 1, 55),
  [LEATHER_CHESTPLATE]: armorDef(LEATHER_CHESTPLATE, "Leather Tunic", "chestplate", "leather", 3, 80),
  [LEATHER_LEGGINGS]: armorDef(LEATHER_LEGGINGS, "Leather Pants", "leggings", "leather", 2, 75),
  [LEATHER_BOOTS]: armorDef(LEATHER_BOOTS, "Leather Boots", "boots", "leather", 1, 65),
  // Iron Armor
  [IRON_HELMET]: armorDef(IRON_HELMET, "Iron Helmet", "helmet", "iron", 2, 165),
  [IRON_CHESTPLATE]: armorDef(IRON_CHESTPLATE, "Iron Chestplate", "chestplate", "iron", 6, 240),
  [IRON_LEGGINGS]: armorDef(IRON_LEGGINGS, "Iron Leggings", "leggings", "iron", 5, 225),
  [IRON_BOOTS]: armorDef(IRON_BOOTS, "Iron Boots", "boots", "iron", 2, 195),
  // Diamond Armor
  [DIAMOND_HELMET]: armorDef(DIAMOND_HELMET, "Diamond Helmet", "helmet", "diamond", 3, 363),
  [DIAMOND_CHESTPLATE]: armorDef(DIAMOND_CHESTPLATE, "Diamond Chestplate", "chestplate", "diamond", 8, 528),
  [DIAMOND_LEGGINGS]: armorDef(DIAMOND_LEGGINGS, "Diamond Leggings", "leggings", "diamond", 6, 495),
  [DIAMOND_BOOTS]: armorDef(DIAMOND_BOOTS, "Diamond Boots", "boots", "diamond", 3, 429),
};

export function isBlockItem(id: number): boolean {
  return id > 0 && id < BLOCKS.length;
}

/** Non-block items available in creative mode / picker. */
export const CREATIVE_ITEMS: readonly number[] = [
  // Combat & Tools
  DIAMOND_SWORD,
  IRON_SWORD,
  STONE_SWORD,
  WOODEN_SWORD,
  DIAMOND_HELMET,
  DIAMOND_CHESTPLATE,
  DIAMOND_LEGGINGS,
  DIAMOND_BOOTS,
  IRON_HELMET,
  IRON_CHESTPLATE,
  IRON_LEGGINGS,
  IRON_BOOTS,
  LEATHER_HELMET,
  LEATHER_CHESTPLATE,
  LEATHER_LEGGINGS,
  LEATHER_BOOTS,
  WOODEN_PICKAXE,
  WOODEN_AXE,
  WOODEN_SHOVEL,
  STONE_PICKAXE,
  STONE_AXE,
  STONE_SHOVEL,
  IRON_PICKAXE,
  IRON_AXE,
  IRON_SHOVEL,
  DIAMOND_PICKAXE,
  DIAMOND_AXE,
  DIAMOND_SHOVEL,
  BOW,
  ARROW,
  // Materials & Drops
  COAL,
  CHARCOAL,
  IRON_INGOT,
  GOLD_INGOT,
  DIAMOND,
  LEATHER,
  GUNPOWDER,
  STICK,
  STRING,
  FEATHER,
  FLINT,
  BONE,
  // Food
  RAW_BEEF,
  COOKED_BEEF,
  RAW_PORKCHOP,
  COOKED_PORKCHOP,
  RAW_MUTTON,
  COOKED_MUTTON,
  SPIDER_EYE,
];

export function isItem(id: number): boolean {
  return isBlockItem(id) || ITEM_DEFS[id] !== undefined;
}

export function itemName(id: number): string {
  if (isBlockItem(id)) return BLOCKS[id].name;
  return ITEM_DEFS[id]?.name ?? "Unknown";
}

/** Stack size for an item id. Tools are deliberately unstackable. */
export function stackLimit(id: number): number {
  if (isBlockItem(id)) return DEFAULT_STACK_LIMIT;
  return ITEM_DEFS[id]?.maxStack ?? DEFAULT_STACK_LIMIT;
}

export function toolInfo(id: number): ToolInfo | null {
  return ITEM_DEFS[id]?.tool ?? null;
}

export function isTool(id: number): boolean {
  return toolInfo(id) !== null;
}

export function isSword(id: number): boolean {
  return toolInfo(id)?.kind === "sword";
}

export function armorInfo(id: number): ArmorInfo | null {
  return ITEM_DEFS[id]?.armor ?? null;
}

export function isArmor(id: number): boolean {
  return armorInfo(id) !== null;
}

export function armorSlot(id: number): ArmorSlot | null {
  return armorInfo(id)?.slot ?? null;
}

/** True for items that can be right-clicked to restore hunger. */
export function isFood(id: number): boolean {
  return ITEM_DEFS[id]?.food !== undefined;
}

/** Food properties for an edible item, if any. */
export function foodItemInfo(id: number): FoodInfo | undefined {
  return ITEM_DEFS[id]?.food;
}

/** Food points restored by an edible item (1 food point = half drumstick). */
export function foodHeal(id: number): number {
  return ITEM_DEFS[id]?.food?.foodLevel ?? 0;
}

/**
 * Melee damage for the selected item. Tools hit harder with better materials,
 * swords deal 3 + tier damage (Minecraft 1.8.8), and axes deal 1 + tier + 1.
 */
export function attackDamage(id: number): number {
  const tool = toolInfo(id);
  if (!tool) return 1;
  if (tool.kind === "sword") return 3 + tool.tier;
  return 1 + tool.tier + (tool.kind === "axe" ? 1 : 0);
}

/** Maximum uses for a tool or armor piece, or 0 for everything else. */
export function maxDurability(id: number): number {
  return toolInfo(id)?.maxDurability ?? armorInfo(id)?.maxDurability ?? 0;
}

/** Build a fresh stack; tools start at full durability. */
export function makeStack(id: number, count = 1): ItemStack {
  const qty = Math.max(1, Math.floor(count));
  const max = maxDurability(id);
  return max > 0 ? { id, count: 1, durability: max } : { id, count: qty };
}

/** Consume uses from a tool. Non-tools pass through; a broken tool is null. */
export function damageStack(stack: ItemStack, amount = 1): ItemStack | null {
  const max = maxDurability(stack.id);
  if (max <= 0) return stack;
  const uses = (stack.durability ?? max) - Math.max(1, Math.floor(amount));
  return uses > 0 ? { ...stack, durability: uses } : null;
}

/** Slot tooltip text: item name plus remaining durability for tools. */
export function describeStack(stack: ItemStack): string {
  const max = maxDurability(stack.id);
  if (max <= 0) return itemName(stack.id);
  const uses = Math.max(0, Math.min(max, stack.durability ?? max));
  return `${itemName(stack.id)} · ${uses}/${max}`;
}
